import React, { useState, useEffect, useRef } from "react";
import "./MyProjectsPart.css";
import ProjectCard from "../projectCard/ProjectCard";
import projectData from "../../static/projects_content.json";
import ModalCarousel from "../modalCarousel/ModalCarousel";
import { ProjectModalContent } from "../../types";
import projectModalsData from "../../static/project_modals_content.json";
import ImageModal from "../imageModal/ImageModal";
import { stringToProjectModalImage } from "../../services/projectModalImageService";

// Local arrow images
import arrowLeft from "../../assets/custom_elements/arrow_left.svg";
import arrowRight from "../../assets/custom_elements/arrow_right.svg";

const MyProjectsPart: React.FC = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [projects] = useState(projectData);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedProjectIndex, setSelectedProjectIndex] = useState(0);
    const [projectCardWidth, setProjectCardWidth] = useState(421);
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);
    const [touchStartY, setTouchStartY] = useState<number | null>(null);
    const [touchEndY, setTouchEndY] = useState<number | null>(null);
    const projectGap = 24;
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [imageModalContent, setImageModalContent] = useState<{
        src: string;
        objectFit: "cover" | "contain" | "fill" | "scale-down" | "none";
        descriptiveTitle?: string;
    } | null>(null);
    const [imageGallery, setImageGallery] = useState<
        {
            src: string;
            objectFit: "cover" | "contain" | "fill" | "scale-down" | "none";
            descriptiveTitle?: string;
        }[]
    >([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // New state for tracking swipe animation
    const [swipeOffset, setSwipeOffset] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const scrollableContainerRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Track if we're currently handling a horizontal swipe
    const [handlingHorizontalSwipe, setHandlingHorizontalSwipe] =
        useState(false);

    // Required minimum distance between touch start and touch end to be detected as swipe
    const minSwipeDistance = 30;
    // Minimum horizontal movement to be considered a horizontal swipe intent
    const minHorizontalIntent = 10;

    // Check if we're on mobile
    const isMobile = () => window.innerWidth <= 700;

    useEffect(() => {
        const updateCardWidth = () => {
            if (window.innerWidth <= 700) {
                setProjectCardWidth(250);
            } else {
                setProjectCardWidth(421);
            }
        };

        updateCardWidth();
        window.addEventListener("resize", updateCardWidth);

        // Add non-passive touch event listeners
        const container = containerRef.current;
        if (container) {
            const handleTouchMovePassive = (e: TouchEvent) => {
                if (handlingHorizontalSwipe) {
                    e.preventDefault();
                }
            };

            // Add event listener with passive: false to allow preventDefault()
            container.addEventListener("touchmove", handleTouchMovePassive, {
                passive: false,
            });

            // Clean up
            return () => {
                window.removeEventListener("resize", updateCardWidth);
                container.removeEventListener(
                    "touchmove",
                    handleTouchMovePassive
                );
            };
        }

        return () => window.removeEventListener("resize", updateCardWidth);
    }, [handlingHorizontalSwipe]);

    const moveDistance = projectCardWidth + projectGap;

    const handleLeftClick = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    const handleRightClick = () => {
        // Allow scrolling all the way to the last project regardless of screen size
        if (currentIndex < projects.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };

    const onTouchStart = (e: React.TouchEvent) => {
        setTouchEnd(null);
        setTouchEndY(null);
        setTouchStart(e.targetTouches[0].clientX);
        setTouchStartY(e.targetTouches[0].clientY);
        setIsAnimating(false);
        setSwipeOffset(0); // Reset any lingering swipe offset
    };

    const onTouchMove = (e: React.TouchEvent) => {
        const touchX = e.targetTouches[0].clientX;
        const touchY = e.targetTouches[0].clientY;

        // Calculate distance moved
        if (touchStart !== null && touchStartY !== null) {
            const horizontalDistance = touchStart - touchX;
            const verticalDistance = Math.abs(touchStartY - touchY);

            // Continuously re-evaluate if this is a horizontal or vertical swipe
            const isCurrentlyHorizontal =
                Math.abs(horizontalDistance) > verticalDistance &&
                Math.abs(horizontalDistance) > minHorizontalIntent;

            // Update the handling state based on current movement direction
            setHandlingHorizontalSwipe(isCurrentlyHorizontal);

            // Only handle horizontal swipes
            if (isCurrentlyHorizontal) {
                // Calculate how much to move based on finger position
                const newOffset = touchStart - touchX;

                // Apply resistance at the edges
                if (
                    (currentIndex === 0 && newOffset < 0) ||
                    (currentIndex === projects.length - 1 && newOffset > 0)
                ) {
                    // Apply 70% resistance at edges
                    setSwipeOffset(newOffset * 0.3);
                } else {
                    setSwipeOffset(newOffset);
                }

                // We're explicitly handling a horizontal swipe, so prevent default browser behavior
                // Don't call preventDefault() directly - we'll use CSS touchAction instead
            }
        }

        setTouchEnd(touchX);
        setTouchEndY(touchY);
    };

    const onTouchEnd = () => {
        // Reset the horizontal swipe handling state
        setHandlingHorizontalSwipe(false);

        if (!touchStart || !touchEnd || !touchStartY || !touchEndY) return;

        const horizontalDistance = touchStart - touchEnd;
        const verticalDistance = Math.abs(touchStartY - touchEndY);

        // Only process as a horizontal swipe if:
        // 1. Horizontal movement is greater than vertical movement
        // 2. Horizontal distance exceeds our threshold
        const isHorizontalSwipe =
            Math.abs(horizontalDistance) > verticalDistance &&
            Math.abs(horizontalDistance) > minSwipeDistance;

        if (isHorizontalSwipe) {
            setIsAnimating(true);

            // Use minSwipeDistance for mobile devices, percentage-based threshold for desktop
            const swipeThreshold = isMobile()
                ? minSwipeDistance
                : moveDistance * 0.3; // 30% threshold for desktop

            if (Math.abs(horizontalDistance) > swipeThreshold) {
                // Swipe was significant enough to change index
                if (
                    horizontalDistance > 0 &&
                    currentIndex < projects.length - 1
                ) {
                    // Swipe left - go to next
                    setCurrentIndex(currentIndex + 1);
                } else if (horizontalDistance < 0 && currentIndex > 0) {
                    // Swipe right - go to previous
                    setCurrentIndex(currentIndex - 1);
                }
            }

            // Reset swipe offset with animation
            setTimeout(() => {
                setSwipeOffset(0);
                // Reset animation state after transition completes
                setTimeout(() => {
                    setIsAnimating(false);
                }, 300);
            }, 0);
        } else {
            // If it was more of a vertical swipe, still reset any swipe offset
            setIsAnimating(true);
            setSwipeOffset(0);
            setTimeout(() => {
                setIsAnimating(false);
            }, 300);
        }
    };

    const handleProjectClick = (index: number) => {
        setSelectedProjectIndex(index);
        setIsModalVisible(true);
    };

    // Handler for opening the image modal
    const handleImageClick = (
        imageSrc: string,
        objectFit: "cover" | "contain" | "fill" | "scale-down" | "none",
        descriptiveTitle?: string
    ) => {
        // Populate the image gallery with all images from the current project
        const currentProject = projectModalsData[selectedProjectIndex];
        const projectImages = currentProject.images.map((img) => {
            const imgSrc = stringToProjectModalImage(img.path);
            return {
                src: imgSrc || "", // Ensure src is always a string
                objectFit: img.objectFit as
                    | "cover"
                    | "contain"
                    | "fill"
                    | "scale-down"
                    | "none",
                descriptiveTitle: (img as any).descriptiveTitle || undefined,
            };
        });

        // Find the index of the clicked image in the gallery
        const clickedImageIndex = projectImages.findIndex(
            (img) => img.src === imageSrc
        );

        setImageGallery(projectImages);
        setCurrentImageIndex(clickedImageIndex >= 0 ? clickedImageIndex : 0);
        setIsImageModalOpen(true);
        setImageModalContent({
            src: imageSrc,
            objectFit: objectFit,
            descriptiveTitle: descriptiveTitle,
        });
    };

    // Handler for when the modal carousel index changes
    const handleModalIndexChange = (newIndex: number) => {
        setSelectedProjectIndex(newIndex);
    };

    // Handler for navigating between images in the gallery
    const handleImageNavigation = (direction: "prev" | "next") => {
        if (!imageGallery.length) return;

        let newIndex = currentImageIndex;
        if (direction === "prev" && currentImageIndex > 0) {
            newIndex = currentImageIndex - 1;
        } else if (
            direction === "next" &&
            currentImageIndex < imageGallery.length - 1
        ) {
            newIndex = currentImageIndex + 1;
        }

        if (newIndex !== currentImageIndex) {
            setCurrentImageIndex(newIndex);
            setImageModalContent({
                src: imageGallery[newIndex].src,
                objectFit: imageGallery[newIndex].objectFit,
                descriptiveTitle: imageGallery[newIndex].descriptiveTitle,
            });
        }
    };

    // Calculate the transform style based on current index and swipe offset
    const getTransformStyle = () => {
        const baseOffset = currentIndex * moveDistance;
        const totalOffset = baseOffset + swipeOffset;

        // Use desktop animation for arrow button navigation, but keep swipe behavior
        // If swipeOffset is 0, we're using arrow buttons or dot navigation
        const isArrowNavigation = swipeOffset === 0 && !isAnimating;

        return {
            transform: `translateX(-${totalOffset}px)`,
            // Use desktop animation for arrow navigation on all devices
            transition: isArrowNavigation
                ? "transform 0.5s ease"
                : isAnimating
                ? "transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
                : "none",
        };
    };

    return (
        <div
            className="my-projects-part-container"
            style={{ touchAction: "pan-y" }}
        >
            <div className="my-projects-part-container-content-container">
                <div className="my-projects-part-title-and-arrows-container">
                    <p className="my-projects-part-text">MY PROJECTS</p>
                    <div className="my-projects-part-arrows-container">
                        <img
                            src={arrowLeft}
                            alt="Left Arrow"
                            className="arrow-icon-projects-page"
                            onClick={handleLeftClick}
                        />
                        <img
                            src={arrowRight}
                            alt="Right Arrow"
                            className="arrow-icon-projects-page"
                            onClick={handleRightClick}
                        />
                    </div>
                </div>
                <div
                    ref={containerRef}
                    className="my-projects-part-projects-visible-container"
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                    style={{
                        touchAction: handlingHorizontalSwipe ? "none" : "pan-y",
                    }}
                >
                    <div
                        ref={scrollableContainerRef}
                        className="my-projects-part-projects-scrollable-container"
                        style={getTransformStyle()}
                    >
                        {projects.map((project, index) => (
                            <div
                                className="my-projects-part-project-card-clickable-container"
                                onClick={() => handleProjectClick(index)}
                                key={index}
                            >
                                <ProjectCard
                                    name={project.name}
                                    description={project.description}
                                    image={project.image}
                                    techLogos={project.tech_logos}
                                />
                            </div>
                        ))}
                    </div>
                </div>
                <div className="select-project-button-container">
                    {projects.map((_, index) => (
                        <div
                            className={
                                index === currentIndex
                                    ? "select-project-button-selected"
                                    : "select-project-button-not-selected"
                            }
                            key={index}
                            onClick={() => setCurrentIndex(index)}
                        ></div>
                    ))}
                </div>
                {isModalVisible && (
                    <ModalCarousel
                        content={projectModalsData as ProjectModalContent[]}
                        onClose={() => {
                            // Only close if image modal is not open
                            if (!isImageModalOpen) {
                                setIsModalVisible(false);
                            }
                        }}
                        initialIndex={selectedProjectIndex}
                        onImageClick={handleImageClick}
                        onIndexChange={handleModalIndexChange}
                    />
                )}
            </div>

            {isImageModalOpen && imageModalContent && (
                <ImageModal
                    imageSrc={imageModalContent.src}
                    objectFit={imageModalContent.objectFit}
                    descriptiveTitle={imageModalContent.descriptiveTitle}
                    onClose={() => setIsImageModalOpen(false)}
                    gallery={imageGallery}
                    currentIndex={currentImageIndex}
                    onNavigate={handleImageNavigation}
                />
            )}
        </div>
    );
};

export default MyProjectsPart;
